import React, { useEffect, useState, useCallback } from 'react';
import { Bell, BellOff, X, CheckCircle2, Calendar, Brain, Loader2 } from 'lucide-react';
import { logger } from '../lib/logger';
import { Button } from './ui/button';

/* PushPermissionPrompt — Phase A round 2
 *
 * Value-first opt-in for Web Push notifications. We DELIBERATELY do NOT
 * call Notification.requestPermission() automatically — that fires a
 * browser-native modal which most athletes dismiss without reading. Instead
 * we show our own branded explainer first ("here's why"), and only call
 * the browser API when the visitor clicks our enabling button.
 *
 * Trigger rule:
 *   • Visible only inside the authenticated /app/* shell (passed by parent)
 *   • Hidden if permission is already 'granted' or 'denied'
 *   • Hidden if dismissed within the last 30 days
 *   • Hidden if running in standalone mode AND the visitor has subscribed
 *     in this browser before
 *   • Hidden if push is not supported (older iOS, etc.)
 */

const SNOOZE_KEY = 'apex.pushPrompt.snoozedUntil.v1';
const SUBSCRIBED_KEY = 'apex.pushPrompt.subscribed.v1';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const isPushSupported = () => {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

const safeRead = (key) => {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null; }
  catch (e) { logger.debug('PushPrompt read blocked', e); return null; }
};

const safeWrite = (key, value) => {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(key, value); }
  catch (e) { logger.debug('PushPrompt write blocked', e); }
};

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const PushPermissionPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // -------------------------------------------------------------------------
  // First-load gate.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isPushSupported()) return undefined;
    if (Notification.permission !== 'default') return undefined;
    if (safeRead(SUBSCRIBED_KEY) === '1') return undefined;
    const snoozed = parseInt(safeRead(SNOOZE_KEY) || '0', 10);
    if (snoozed && Date.now() < snoozed) return undefined;
    const timer = setTimeout(() => setVisible(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  const snooze = useCallback(() => {
    const until = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
    safeWrite(SNOOZE_KEY, String(until));
    setVisible(false);
  }, []);

  const enable = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Step 1: fetch VAPID public key.
      const keyRes = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
      if (!keyRes.ok) throw new Error('vapid_fetch_failed');
      const { public_key: publicKey, configured } = await keyRes.json();
      if (!configured || !publicKey) throw new Error('vapid_not_configured');

      // Step 2: ensure SW is registered (it already is via index.js, but
      // double-check so we don't subscribe against a stale registration).
      const reg = await navigator.serviceWorker.ready;

      // Step 3: trigger the browser's permission prompt.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        snooze();
        return;
      }

      // Step 4: subscribe with our VAPID key.
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // Step 5: persist subscription on backend.
      const sub = subscription.toJSON();
      const post = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: sub.keys,
          user_agent: (navigator.userAgent || '').slice(0, 400),
        }),
      });
      if (!post.ok) throw new Error(`subscribe_${post.status}`);

      safeWrite(SUBSCRIBED_KEY, '1');
      setSuccess(true);
      // Auto-dismiss after a brief confirmation moment.
      setTimeout(() => setVisible(false), 2200);
    } catch (err) {
      logger.warn('Push enable failed', err);
      // Soft-fail: snooze so we don't pester the user immediately.
      snooze();
    } finally {
      setSubmitting(false);
    }
  }, [submitting, snooze]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Enable Apex notifications"
      data-testid="push-permission-prompt"
      className="rounded-2xl border border-[#004C54]/20 bg-white shadow-sm p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="shrink-0 h-11 w-11 rounded-xl bg-[#004C54] text-white inline-flex items-center justify-center">
            {success ? <CheckCircle2 className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-bold mb-1">
              {success ? "You're in" : 'Optional · turn off anytime'}
            </div>
            <h3
              className="font-heading text-[16px] sm:text-[17px] font-bold text-foreground leading-snug"
              data-testid="push-prompt-title"
            >
              {success
                ? "Notifications on. We'll keep it sharp."
                : 'Want Coach Lewis to reach you when it matters?'}
            </h3>
            {!success && (
              <p className="text-[12.5px] text-foreground/65 mt-1.5 leading-snug max-w-[460px]">
                Pre-game reminders 2 hours before competition, a Monday mindset rep, and journal nudges after tough losses &mdash; nothing else.
              </p>
            )}
          </div>
        </div>
        {!success && (
          <button
            type="button"
            onClick={snooze}
            aria-label="Dismiss notification prompt"
            data-testid="push-prompt-dismiss"
            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full text-foreground/35 hover:text-foreground hover:bg-secondary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!success && (
        <>
          {/* Concrete examples — value-first, not a generic "stay updated" pitch */}
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { icon: <Calendar className="h-3.5 w-3.5" />, label: '2h before game cue' },
              { icon: <Brain className="h-3.5 w-3.5" />, label: 'Monday mindset rep' },
              { icon: <BellOff className="h-3.5 w-3.5" />, label: 'Off any time' },
            ].map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-2 rounded-lg border border-[#004C54]/10 bg-[#004C54]/5 px-3 py-2 text-[12px] text-foreground/75"
              >
                <span className="text-[#004C54]">{item.icon}</span>
                {item.label}
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              onClick={enable}
              disabled={submitting}
              size="sm"
              className="h-10 px-5 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
              data-testid="push-prompt-enable"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Enabling
                </>
              ) : (
                <>
                  <Bell className="h-3.5 w-3.5 mr-2" />
                  Turn on notifications
                </>
              )}
            </Button>
            <button
              type="button"
              onClick={snooze}
              className="text-[12px] text-foreground/45 hover:text-foreground/70"
              data-testid="push-prompt-later"
            >
              Not right now
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PushPermissionPrompt;
